import Bugsnag from "@bugsnag/js";
import { useEffect, useState } from "react";
import { FiChevronRight } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import nullStateImg from "../../assets/notifications/notifications-null-state.svg";
import axiosInstance from "../../axiosInstance";
import { trackEvent } from "../../helperFunctions";
import useNotificationStatus from "../../hooks/useNotificationStatus";
import useOutsideAlert from "../../hooks/useOutsideAlert";
import {
  setHasNewNotification,
  setUnreadNotificationsCount,
} from "../../Store/Actions";
import { ADD_TOAST } from "../../Store/Actions/types";
import URL from "../../urls";
import { IconButton } from "../common/Buttons";
import {
  DAVYS_GREY_100,
  DAVYS_GREY_300,
  DAVYS_GREY_400,
} from "../common/colors";
import FlexBox from "../common/FlexBox";
import { H3 } from "../common/Headings";
import Loader from "../common/Loader";
import NullState from "../common/NullState";
import { boxShadowDs1 } from "../common/styles";
import NotificationsListItem from "./NotificationsListItem";
import PausedNotificationsBanner from "./PausedNotificationsBanner";

const Container = styled(FlexBox)`
  flex-direction: column;
  position: absolute;
  top: 2.25rem;
  right: 0;
  width: 30rem;
  height: ${({ showNullState }) => (showNullState ? "auto" : "40rem")};
  background-color: ${DAVYS_GREY_100};
  border: 1px solid ${DAVYS_GREY_400};
  border-radius: 1rem;
  overflow: hidden;
  ${boxShadowDs1}
`;

const Header = styled(FlexBox)`
  padding: 1rem;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid ${DAVYS_GREY_300};
`;

const NotificationsList = styled(FlexBox)`
  flex: 1;
  flex-direction: column;
  overflow-y: auto;
`;

const NotificationsPopup = ({
  containerRef,
  setShowNotificationsPopup,
  commonAnalyticsPayload,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { areNotificationsPaused, hasUnreadNotification } = useSelector(
    state => ({
      areNotificationsPaused: state.notifications.areNotificationsPaused,
      hasUnreadNotification: state.notifications.hasUnreadNotification,
    })
  );

  const [isLoading, setIsLoading] = useState(false);
  const [notifications, setNotifications] = useState(false);
  const [reloadSwitch, setReloadSwitch] = useState(false);

  useNotificationStatus();

  useOutsideAlert(
    containerRef,
    () => setShowNotificationsPopup(false),
    "modalPortal"
  );

  useEffect(() => {
    if (hasUnreadNotification) setReloadSwitch(prev => !prev);
  }, [hasUnreadNotification]);

  useEffect(() => {
    const controller = new AbortController();
    const getNotifications = async () => {
      setIsLoading(true);
      try {
        const response = await axiosInstance.get(URL.notifications, {
          params: { page: 1, limit: 10 },
          signal: controller.signal,
        });
        const notificationsData = response?.data;
        const fetchedNotifications = notificationsData?.data || [];
        setNotifications(fetchedNotifications);
        dispatch(setHasNewNotification(false));
        dispatch(
          setUnreadNotificationsCount(
            notificationsData?.unread_notifications_count
          )
        );
        setIsLoading(false);
      } catch (error) {
        if (error?.message === "canceled") return;
        setIsLoading(false);
        Bugsnag.notify(error);
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: "Failed to fetch notifications",
            appearance: "error",
          },
        });
      }
    };
    getNotifications();
    return () => controller.abort();
  }, [reloadSwitch, dispatch]);

  const handleRedirection = () => {
    trackEvent({
      event: "notification_all_click",
      payload: { ...commonAnalyticsPayload, source: "dropdown" },
    });
    navigate("/notifications");
    setShowNotificationsPopup(false);
  };

  return (
    <Container showNullState={!isLoading && notifications.length === 0}>
      <Header>
        <H3 bold>Notifications</H3>
        <IconButton
          textCta
          iconPosition="right"
          Icon={FiChevronRight}
          onClick={handleRedirection}
        >
          VIEW ALL
        </IconButton>
      </Header>
      {areNotificationsPaused && <PausedNotificationsBanner />}
      <NotificationsList className="hide-scrollbar">
        {isLoading ? (
          <Loader />
        ) : notifications.length > 0 ? (
          notifications.map(notification => (
            <NotificationsListItem
              key={notification?.id}
              notification={notification}
              source="dropdown"
              closePopup={() => setShowNotificationsPopup(false)}
            />
          ))
        ) : (
          <NullState
            text="No notifications yet"
            subtext="There are no notifications to display."
            imgSrc={nullStateImg}
            imgWidth="7.5rem"
            imgHeight="7.5rem"
            rowGap="1.5rem"
          />
        )}
      </NotificationsList>
    </Container>
  );
};

export default NotificationsPopup;
